import React from "react";
import {
  ArrowLeft,
  BookOpen,
  Scale,
  Brain,
  FileText,
  Clock,
  Home,
  LayoutDashboard,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@core/components/ui/button";
import { ProductHeader } from "@core/components/layout/products/ProductHeader";
import { ProductSidebar } from "@core/components/layout/products/ProductSidebar";
import { DocsViewer } from "./DocsViewer";
import { useLegalNavigation } from "@core/hooks/useLegalNavigation";
import type { ProductType } from "@core/config/products.config";

interface MarkdownDocPageProps {
  productType: ProductType;
  docPath: string;
  title?: string;
  subtitle?: string;
  icon?: "legal" | "docs" | "brain" | "clock";
}

const themeMap: Record<ProductType, "blue" | "violet" | "emerald" | "amber"> = {
  date: "blue",
  form: "violet",
  availability: "emerald",
  quizz: "amber",
};

const themeGradients: Record<ProductType, string> = {
  date: "from-blue-400 to-indigo-400",
  form: "from-purple-400 to-pink-400",
  availability: "from-emerald-400 to-teal-400",
  quizz: "from-amber-400 to-orange-400",
};

const themeBadge: Record<ProductType, string> = {
  date: "bg-blue-500/10 border-blue-500/20 text-blue-300",
  form: "bg-violet-500/10 border-violet-500/20 text-violet-300",
  availability: "bg-emerald-500/10 border-emerald-500/20 text-emerald-300",
  quizz: "bg-amber-500/10 border-amber-500/20 text-amber-300",
};

const themeIconBg: Record<ProductType, string> = {
  date: "text-blue-400",
  form: "text-violet-400",
  availability: "text-emerald-400",
  quizz: "text-amber-400",
};

const iconMap = {
  legal: Scale,
  docs: BookOpen,
  brain: Brain,
  clock: Clock,
};

/**
 * Page wrapper réutilisable pour afficher un document markdown
 * avec ProductHeader + ProductSidebar dans le layout standard.
 * Style aligné avec les pages Documentation standalone.
 */
export const MarkdownDocPage: React.FC<MarkdownDocPageProps> = ({
  productType,
  docPath,
  title,
  subtitle,
  icon = "docs",
}) => {
  const navigate = useNavigate();
  const navigation = useLegalNavigation(productType);
  const IconComponent = iconMap[icon] || BookOpen;

  return (
    <div className="flex flex-col min-h-screen-safe bg-[#030712] text-white">
      <ProductHeader productType={productType} showAuth={true} />
      <div className="flex flex-1">
        <ProductSidebar productType={productType} />
        <div className="flex-1">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            {/* Breadcrumbs */}
            {/* <nav className="flex items-center space-x-2 text-sm text-gray-400 mb-6">
              {navigation.breadcrumbs.map((crumb, index) => (
                <React.Fragment key={crumb.href}>
                  {index > 0 && <span>/</span>}
                  {index === navigation.breadcrumbs.length - 1 ? (
                    <span className="text-white">{crumb.label}</span>
                  ) : (
                    <a
                      href={crumb.href}
                      className="hover:text-white transition-colors"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(crumb.href);
                      }}
                    >
                      {crumb.label}
                    </a>
                  )}
                </React.Fragment>
              ))}
            </nav> */}

            {/* Header */}
            <div className="text-center mb-12">
              <div
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-6 ${themeBadge[productType]}`}
              >
                <IconComponent
                  className={`w-4 h-4 ${themeIconBg[productType]}`}
                />
                <span className="text-sm">{title || "Document"}</span>
              </div>
              {title && (
                <h1
                  className={`text-4xl font-extrabold tracking-tight mb-4 bg-clip-text text-transparent bg-gradient-to-r ${themeGradients[productType]}`}
                >
                  {title}
                </h1>
              )}
              {subtitle && (
                <p className="text-lg text-gray-400 max-w-2xl mx-auto">
                  {subtitle}
                </p>
              )}
            </div>

            {/* Content Card */}
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-8 md:p-12">
              <DocsViewer docPath={docPath} theme={themeMap[productType]} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
